import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { imageUrl } from '../config/images';
import SiteHeader from './SiteHeader';
import PosterPlaceholder from './PosterPlaceholder';
import { sectionEndpoint } from '../config/sectionTypes';

// Paginated grid of film/TV cards for a single carousel section — the "View all"
// target. The section descriptor is carried in the ?s= query param (see
// config/sectionTypes.js browseHref); pagination uses the same endpoint + page.
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const PAGE_SIZE = 24;

function decodeSection(s) {
  try {
    return JSON.parse(decodeURIComponent(s));
  } catch (e) {
    return null;
  }
}

export default function BrowseList() {
  const [params, setParams] = useSearchParams();
  const sParam = params.get('s');
  const page = Math.max(1, parseInt(params.get('page') || '1', 10));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const section = decodeSection(sParam);

  const load = useCallback(async () => {
    const sec = decodeSection(sParam);
    const url = sec && sectionEndpoint(sec, { limit: PAGE_SIZE, page });
    if (!url) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}${url}`);
      const json = r.ok ? await r.json() : {};
      setItems(json.results || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [sParam, page]);

  useEffect(() => { load(); window.scrollTo(0, 0); }, [load]);

  const goto = (p) => setParams({ s: sParam, page: String(p) });
  const hasNext = items.length === PAGE_SIZE;

  const btn = (enabled) => `inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium ${
    enabled ? 'bg-videbits-50 text-videbits-700 hover:bg-videbits-100' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
  }`;

  return (
    <div className="min-h-screen bg-gray-50">
      <SiteHeader />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">{section?.title || 'Browse'}</h1>

        {!section ? (
          <p className="text-gray-400 text-sm">Nothing to browse.</p>
        ) : loading ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <div key={i} className="aspect-[2/3] rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="text-gray-400 text-sm">No results{page > 1 ? ' on this page' : ''}.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
            {items.map((item) => {
              const poster = item.poster_path
                ? imageUrl(item.poster_path, 'w300')
                : null;
              return (
                <Link
                  key={item.id}
                  to={item.category === 'TV Series' ? `/tv/${item.id}` : `/movie/${item.id}`}
                  onClick={() => sessionStorage.setItem(`movie_${item.id}`, JSON.stringify(item))}
                  className="group"
                >
                  <div className="aspect-[2/3] rounded-xl overflow-hidden bg-gray-100 mb-2 shadow-sm group-hover:shadow-md transition-all duration-200">
                    {poster ? (
                      <img src={poster} alt={item.title}
                           className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                    ) : (
                      <PosterPlaceholder />
                    )}
                  </div>
                  <p className="text-xs font-medium text-gray-800 line-clamp-2 leading-tight">{item.title}</p>
                  {item.vote_average > 0 && (
                    <p className="text-xs text-gray-400 mt-0.5">★ {item.vote_average.toFixed(1)}</p>
                  )}
                </Link>
              );
            })}
          </div>
        )}

        {section && !loading && (page > 1 || hasNext) && (
          <div className="flex items-center justify-center gap-4 mt-8">
            <button type="button" disabled={page <= 1} onClick={() => goto(page - 1)} className={btn(page > 1)}>
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            <span className="text-sm text-gray-500">Page {page}</span>
            <button type="button" disabled={!hasNext} onClick={() => goto(page + 1)} className={btn(hasNext)}>
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
